import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface AyarState {
  aktifMi: boolean;
  saglayici: string;
  apiUrl: string;
  modelAdi: string;
  timeoutSaniye: number;
  apiKeyVarMi: boolean;
  apiKeyMaskeli: string;
}

const SAGLAYICI_ONAYAR: Record<string, { apiUrl: string; modelAdi: string }> = {
  DeepSeek: { apiUrl: 'https://api.deepseek.com/chat/completions', modelAdi: 'deepseek-chat' },
  OpenAI: { apiUrl: 'https://api.openai.com/v1/chat/completions', modelAdi: 'gpt-4o-mini' },
};

const YapayZekaAyarlari: React.FC = () => {
  const [ayar, setAyar] = useState<AyarState>({
    aktifMi: false, saglayici: 'DeepSeek',
    apiUrl: 'https://api.deepseek.com/chat/completions', modelAdi: 'deepseek-chat',
    timeoutSaniye: 15, apiKeyVarMi: false, apiKeyMaskeli: '',
  });
  const [yeniApiKey, setYeniApiKey] = useState('');
  const [keyDirty, setKeyDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testSonuc, setTestSonuc] = useState<{ basarili: boolean; mesaj: string } | null>(null);

  const fetchAyar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/YapayZekaAyarlari');
      setAyar({
        aktifMi: !!res.data.aktifMi,
        saglayici: res.data.saglayici || 'DeepSeek',
        apiUrl: res.data.apiUrl || '',
        modelAdi: res.data.modelAdi || '',
        timeoutSaniye: res.data.timeoutSaniye ?? 15,
        apiKeyVarMi: !!res.data.apiKeyVarMi,
        apiKeyMaskeli: res.data.apiKeyMaskeli || '',
      });
    } catch {
      toast.error('Ayarlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAyar(); }, [fetchAyar]);

  const handleSaglayiciDegis = (s: string) => {
    const onayar = SAGLAYICI_ONAYAR[s];
    setAyar(p => ({ ...p, saglayici: s, ...(onayar ? { apiUrl: onayar.apiUrl, modelAdi: onayar.modelAdi } : {}) }));
  };

  const handleKaydet = async (apiKeyTemizle = false) => {
    setSaving(true);
    setTestSonuc(null);
    try {
      await axiosInstance.post('/api/YapayZekaAyarlari/update/1', {
        aktifMi: ayar.aktifMi,
        saglayici: ayar.saglayici,
        apiUrl: ayar.apiUrl,
        modelAdi: ayar.modelAdi,
        timeoutSaniye: ayar.timeoutSaniye,
        // Anahtar yalnızca gerçekten yazıldıysa gönderilir; boş gönderimde sunucu mevcudu korur.
        yeniApiKey: !apiKeyTemizle && keyDirty ? yeniApiKey : null,
        apiKeyTemizle,
      });
      toast.success('Ayarlar kaydedildi.');
      setYeniApiKey('');
      setKeyDirty(false);
      fetchAyar();
    } catch {
      toast.error('Kaydetme sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestSonuc(null);
    try {
      const res = await axiosInstance.post('/api/YapayZekaAyarlari/test');
      setTestSonuc({ basarili: !!res.data.basarili, mesaj: res.data.mesaj || '' });
    } catch {
      setTestSonuc({ basarili: false, mesaj: 'Test isteği gönderilemedi.' });
    } finally {
      setTesting(false);
    }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: 6 };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', background: '#f4f6fb', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: '#1a2440', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="fas fa-brain" style={{ color: '#e67d21', fontSize: 18 }}></i>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Yapay Zeka Ayarları</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Müşteri Sunumları'ndaki "Akıllı Arama" için sağlayıcı ve API anahtarı
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 24, maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Durum</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Pasifken Akıllı Arama yerel çözümleme ile çalışmaya devam eder.</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: ayar.aktifMi ? '#059669' : '#94a3b8' }}>{ayar.aktifMi ? 'Aktif' : 'Pasif'}</span>
            <span
              onClick={() => setAyar(p => ({ ...p, aktifMi: !p.aktifMi }))}
              style={{ width: 42, height: 24, borderRadius: 12, background: ayar.aktifMi ? '#059669' : '#cbd5e1', position: 'relative', transition: 'background 0.15s ease', flexShrink: 0 }}
            >
              <span style={{ position: 'absolute', top: 2, left: ayar.aktifMi ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </span>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={lbl}>Sağlayıcı</label>
            <select value={ayar.saglayici} onChange={e => handleSaglayiciDegis(e.target.value)} style={inp}>
              <option value="DeepSeek">DeepSeek</option>
              <option value="OpenAI">OpenAI</option>
              <option value="Diger">Diğer (OpenAI uyumlu)</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Model Adı</label>
            <input type="text" value={ayar.modelAdi} onChange={e => setAyar(p => ({ ...p, modelAdi: e.target.value }))} style={inp} placeholder="ör: deepseek-chat" />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={lbl}>API Adresi</label>
          <input type="text" value={ayar.apiUrl} onChange={e => setAyar(p => ({ ...p, apiUrl: e.target.value }))} style={inp} placeholder="https://api.deepseek.com/chat/completions" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <label style={lbl}>
              API Anahtarı
              {ayar.apiKeyVarMi && <span style={{ marginLeft: 8, fontWeight: 500, color: '#059669' }}><i className="fas fa-check-circle" style={{ marginRight: 4 }} />kayıtlı</span>}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={yeniApiKey}
              onChange={e => { setYeniApiKey(e.target.value); setKeyDirty(true); }}
              style={inp}
              placeholder={ayar.apiKeyVarMi ? ayar.apiKeyMaskeli : 'sk-...'}
            />
            {ayar.apiKeyVarMi && (
              <button
                onClick={() => handleKaydet(true)}
                disabled={saving}
                style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, color: '#dc2626', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Anahtarı sil
              </button>
            )}
          </div>
          <div>
            <label style={lbl}>Zaman Aşımı (sn)</label>
            <input
              type="number" min={5} max={60} step={1}
              value={ayar.timeoutSaniye}
              onChange={e => setAyar(p => ({ ...p, timeoutSaniye: parseInt(e.target.value) || 15 }))}
              style={inp}
            />
          </div>
        </div>

        <div style={{ marginTop: 18, padding: '12px 14px', background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 10, fontSize: 12, color: '#92400e', lineHeight: 1.55 }}>
          <i className="fas fa-shield-halved" style={{ marginRight: 6 }}></i>
          API anahtarı yalnızca sunucuda saklanır, tarayıcıya hiçbir zaman gönderilmez. Akıllı Arama'da yapay zekaya
          <b> sadece kullanıcının yazdığı cümle</b> iletilir; müşteri, daire veya portföy verisi gönderilmez.
          Eşleştirme ve sıralama tamamen kendi sunucunuzdaki veriyle yapılır.
        </div>

        {testSonuc && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 10, fontSize: 12.5, fontWeight: 500,
            background: testSonuc.basarili ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${testSonuc.basarili ? '#86efac' : '#fecaca'}`,
            color: testSonuc.basarili ? '#15803d' : '#b91c1c',
          }}>
            <i className={`fas fa-${testSonuc.basarili ? 'circle-check' : 'circle-exclamation'}`} style={{ marginRight: 6 }} />
            {testSonuc.mesaj}
          </div>
        )}

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={handleTest}
            disabled={testing || saving}
            style={{ padding: '10px 18px', border: '1px solid #d1d5db', borderRadius: 9, background: '#fff', color: '#374151', fontSize: 13.5, fontWeight: 600, cursor: testing ? 'not-allowed' : 'pointer' }}
          >
            <i className={testing ? 'fas fa-spinner fa-spin' : 'fas fa-plug'} style={{ marginRight: 6 }}></i>
            {testing ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
          </button>
          <button
            onClick={() => handleKaydet(false)}
            disabled={saving}
            style={{ padding: '10px 22px', border: 'none', borderRadius: 9, background: saving ? '#94a3b8' : '#1a2440', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            <i className="fas fa-save" style={{ marginRight: 6 }}></i>{saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YapayZekaAyarlari;
